import React, { useEffect, useMemo, useState } from 'react';
import { Link2, Loader2, Pencil, Search, Unlink } from 'lucide-react';
import { Modal } from '../../../reusable';
import { getDocumentSaleCandidates, setRegisterSaleLink } from '../../../../services/treasuryAPI';

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  const iso = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};

const scoreColor = (score) => {
  if (score >= 90) return '#16a34a';
  if (score >= 60) return '#d97706';
  return '#dc2626';
};

const CHECK_LABELS = {
  folio: { match: 'Coincide', miss: 'Distinto' },
  rut: { match: 'Coincide', miss: 'Distinto' },
  amount: { exact: 'Exacto (neto o c/IVA)', near: 'Cercano (±1%)', miss: 'Distinto' },
  date: { match: 'Dentro de 30 días', miss: 'Más de 30 días' },
};

const checkTone = (value) => {
  if (value === 'match' || value === 'exact') return '#16a34a';
  if (value === 'near') return '#d97706';
  if (value === 'miss') return '#dc2626';
  return '#94a3b8';
};

const AccountingDocumentSaleReconcileModal = ({
  isOpen = false,
  document = null,
  onClose,
  onLinked,
  onEditDocument,
  isDarkMode = false,
  ui,
}) => {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [currentDoc, setCurrentDoc] = useState(document);

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setQuery('');
    setError('');
    setCurrentDoc(document);
    setSelectedId(Number(document?.sale_id || document?.suggested_sale_id || 0) || null);
  }, [isOpen, document]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handle = window.setTimeout(() => setQuery(search.trim()), search ? 250 : 0);
    return () => window.clearTimeout(handle);
  }, [search, isOpen]);

  useEffect(() => {
    if (!isOpen || !document?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    getDocumentSaleCandidates(document.id, query)
      .then((payload) => {
        if (cancelled) return;
        const list = Array.isArray(payload?.candidates) ? payload.candidates : [];
        setCandidates(list);
        if (payload?.document) setCurrentDoc(payload.document);
        setSelectedId((current) => {
          if (current && list.some((item) => Number(item.id) === Number(current))) return current;
          const linked = list.find((item) => item.is_linked);
          if (linked) return Number(linked.id);
          const suggested = Number(payload?.document?.suggested_sale_id || document?.suggested_sale_id || 0);
          if (suggested && list.some((item) => Number(item.id) === suggested)) return suggested;
          return list[0] ? Number(list[0].id) : null;
        });
        setError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setCandidates([]);
        setError(err?.message || 'No se pudo cargar el listado de ventas.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, document?.id, query]);

  const selected = useMemo(
    () => candidates.find((item) => Number(item.id) === Number(selectedId)) || null,
    [candidates, selectedId]
  );

  const text = ui?.text || (isDarkMode ? '#f4f4f5' : '#111827');
  const muted = ui?.muted || (isDarkMode ? '#a1a1aa' : '#6b7280');
  const border = ui?.border || (isDarkMode ? '#3f3f46' : '#e5e7eb');
  const headerBg = ui?.header || (isDarkMode ? '#18181b' : '#f4f4f5');
  const selectedBg = isDarkMode ? 'rgba(37, 99, 235, 0.18)' : '#eff6ff';
  const linkedId = Number(currentDoc?.sale_id || 0);

  const btn = (variant, enabled = true) => {
    const filled = variant === 'success' || variant === 'danger';
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 36,
      padding: '0 14px',
      borderRadius: 8,
      border: filled ? 'none' : `1px solid ${ui?.inputBorder || border}`,
      background: variant === 'success' ? '#16a34a' : variant === 'danger' ? '#dc2626' : ui?.inputBg || 'transparent',
      color: filled ? '#fff' : text,
      fontWeight: 600,
      fontSize: 13,
      cursor: enabled ? 'pointer' : 'not-allowed',
      opacity: enabled ? 1 : 0.55,
    };
  };

  const th = () => ({
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: muted,
    borderBottom: `1px solid ${border}`,
    background: headerBg,
    position: 'sticky',
    top: 0,
  });
  const td = (align = 'left') => ({
    padding: '8px 10px',
    fontSize: 12,
    color: text,
    textAlign: align,
    borderBottom: `1px solid ${border}`,
    verticalAlign: 'top',
  });

  const handleLink = async () => {
    if (!currentDoc?.id || !selected?.id) return;
    setSaving(true);
    setError('');
    try {
      const result = await setRegisterSaleLink(currentDoc.id, {
        sale_id: Number(selected.id),
        link_method: 'manual',
        match_score: Number(selected.match_score || 0) || null,
        notes: '',
      });
      const updated = result?.document || { ...currentDoc, sale_id: selected.id, sale_code: selected.sale_code };
      setCurrentDoc(updated);
      setCandidates((list) =>
        list.map((item) => ({ ...item, is_linked: Number(item.id) === Number(selected.id) }))
      );
      await onLinked?.(updated);
    } catch (err) {
      setError(err?.message || 'No se pudo vincular la venta.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!currentDoc?.id || !linkedId) return;
    if (!window.confirm(`¿Quitar el vínculo con ${currentDoc.sale_code || 'la venta'}?`)) return;
    setSaving(true);
    setError('');
    try {
      const result = await setRegisterSaleLink(currentDoc.id, { sale_id: null, link_method: 'manual' });
      const updated = result?.document || { ...currentDoc, sale_id: null, sale_code: '' };
      setCurrentDoc(updated);
      setCandidates((list) => list.map((item) => ({ ...item, is_linked: false })));
      await onLinked?.(updated);
    } catch (err) {
      setError(err?.message || 'No se pudo desvincular la venta.');
    } finally {
      setSaving(false);
    }
  };

  const check = (key) => {
    const value = selected?.match_checks?.[key] || 'na';
    return (
      <span style={{ fontWeight: 700, color: checkTone(value), whiteSpace: 'nowrap' }}>
        {CHECK_LABELS[key][value] || 'Sin dato'}
      </span>
    );
  };

  const saleNet = Number(selected?.matched_document?.net_amount || selected?.total_amount || 0);
  const saleTotal = Number(selected?.matched_document?.total_amount || 0) || (saleNet ? saleNet * 1.19 : 0);
  const sign = Number(currentDoc?.total_amount || 0) < 0 ? -1 : 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title="Conciliar con ventas"
      subtitle={
        currentDoc
          ? `${currentDoc.document_type || 'Documento'} ${currentDoc.doc_number || ''} · ${
              currentDoc.sale_code ? `vinculado a ${currentDoc.sale_code}` : 'sin venta vinculada'
            }`
          : 'Elige una venta comercial para este documento del libro.'
      }
      size="xxl"
      isDarkMode={isDarkMode}
      closeOnOverlayClick={!saving}
      closeOnEscape={!saving}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          {onEditDocument && currentDoc ? (
            <button type="button" onClick={() => onEditDocument(currentDoc)} disabled={saving} style={btn('ghost', !saving)}>
              <Pencil size={14} /> Editar documento
            </button>
          ) : null}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {linkedId ? (
              <button type="button" onClick={handleUnlink} disabled={saving} style={btn('danger', !saving)}>
                <Unlink size={14} /> Desvincular
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleLink}
              disabled={saving || !selected || Number(selected.id) === linkedId}
              style={btn('success', !saving && selected && Number(selected.id) !== linkedId)}
            >
              {saving ? <Loader2 size={16} /> : <Link2 size={16} />}
              {saving
                ? 'Vinculando…'
                : Number(selected?.id) === linkedId
                  ? 'Ya vinculada'
                  : `Vincular ${selected?.sale_code || 'venta'}`}
            </button>
            <button type="button" onClick={onClose} disabled={saving} style={btn('ghost', !saving)}>
              Cerrar
            </button>
          </div>
        </div>
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.1fr)',
          gap: 12,
          height: '62vh',
          minHeight: 380,
        }}
      >
        <section
          style={{
            border: `1px solid ${border}`,
            borderRadius: 12,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateRows: 'auto auto 1fr',
            minWidth: 0,
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${border}`, background: headerBg }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: text }}>Listado de ventas</div>
            <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
              Busca por código, cliente, RUT o folio. Sin búsqueda se muestran las más cercanas a este documento.
            </div>
          </div>
          <div style={{ padding: 10, borderBottom: `1px solid ${border}` }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Search size={14} color={muted} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="V001, cliente, RUT, folio…"
                style={{
                  flex: 1,
                  height: 34,
                  borderRadius: 8,
                  border: `1px solid ${ui?.inputBorder || border}`,
                  background: ui?.inputBg || 'transparent',
                  color: text,
                  padding: '0 10px',
                  fontSize: 13,
                }}
              />
            </label>
          </div>
          <div style={{ overflow: 'auto' }}>
            {loading ? (
              <p style={{ margin: 16, fontSize: 12, color: muted }}>Cargando ventas…</p>
            ) : !candidates.length ? (
              <p style={{ margin: 16, fontSize: 12, color: muted }}>
                No hay ventas que coincidan. Prueba buscando el código o el cliente.
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th()}>Venta</th>
                    <th style={th()}>Cliente</th>
                    <th style={{ ...th(), textAlign: 'right' }}>Monto neto</th>
                    <th style={{ ...th(), textAlign: 'right' }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((row) => {
                    const active = Number(row.id) === Number(selectedId);
                    return (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedId(Number(row.id))}
                        style={{
                          background: active ? selectedBg : 'transparent',
                          cursor: 'pointer',
                        }}
                      >
                        <td style={td()}>
                          <div style={{ fontWeight: 700 }}>{row.sale_code || `ID ${row.id}`}</div>
                          <div style={{ fontSize: 11, color: muted }}>
                            {row.invoice_number ? `Folio ${row.invoice_number}` : 'Sin folio'}
                            {' · '}
                            {formatDate(row.invoice_date || row.deal_close_date)}
                          </div>
                          {row.is_linked ? (
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>Vinculada</div>
                          ) : null}
                        </td>
                        <td style={td()}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                            {row.organization_name || '—'}
                          </div>
                          <div style={{ fontSize: 11, color: muted }}>{row.organization_rut || ''}</div>
                        </td>
                        <td style={{ ...td('right'), whiteSpace: 'nowrap' }}>{money(row.total_amount)}</td>
                        <td style={{ ...td('right'), fontWeight: 700, color: scoreColor(row.match_score) }}>
                          {Math.round(Number(row.match_score || 0))}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section
          style={{
            border: `1px solid ${border}`,
            borderRadius: 12,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
            minWidth: 0,
          }}
        >
          <div
            style={{
              padding: '10px 12px',
              borderBottom: `1px solid ${border}`,
              background: headerBg,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: text }}>
                {`${currentDoc?.document_type || 'Documento'} ${currentDoc?.doc_number || ''}`.trim()}
                {selected ? ` ↔ ${selected.sale_code}` : ''}
              </div>
              <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
                {currentDoc?.counterpart_name || 'Sin contraparte'} · {formatDate(currentDoc?.doc_date)}
              </div>
            </div>
            {selected ? (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: muted }}>Coincidencia</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: scoreColor(selected.match_score), lineHeight: 1.1 }}>
                  {Math.round(Number(selected.match_score || 0))}%
                </div>
              </div>
            ) : null}
          </div>
          <div style={{ overflow: 'auto' }}>
            {!selected ? (
              <p style={{ margin: 16, fontSize: 12, color: muted }}>Selecciona una venta a la izquierda para comparar.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th()}>Campo</th>
                    <th style={th()}>Documento del libro</th>
                    <th style={th()}>Venta comercial</th>
                    <th style={th()}>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      label: 'Folio',
                      doc: currentDoc?.doc_number,
                      sale: selected.matched_document?.doc_number || selected.invoice_number,
                      result: check('folio'),
                    },
                    {
                      label: 'Fecha',
                      doc: formatDate(currentDoc?.doc_date),
                      sale: formatDate(selected.matched_document?.doc_date || selected.invoice_date || selected.deal_close_date),
                      result: check('date'),
                    },
                    { label: 'Cliente', doc: currentDoc?.counterpart_name, sale: selected.organization_name },
                    {
                      label: 'RUT',
                      doc: currentDoc?.counterpart_rut,
                      sale: selected.organization_rut,
                      result: check('rut'),
                    },
                    {
                      label: 'Neto',
                      doc: money(currentDoc?.net_amount),
                      sale: saleNet ? money(sign * saleNet) : '—',
                    },
                    {
                      label: 'Total c/IVA',
                      doc: money(currentDoc?.total_amount),
                      sale: saleTotal ? money(sign * saleTotal) : '—',
                      result: check('amount'),
                      strong: true,
                    },
                  ].map((row) => (
                    <tr key={row.label}>
                      <td style={{ ...td(), color: muted, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.label}</td>
                      <td style={{ ...td(), fontWeight: row.strong ? 700 : 500 }}>{row.doc || '—'}</td>
                      <td style={{ ...td(), fontWeight: row.strong ? 700 : 500 }}>{row.sale || '—'}</td>
                      <td style={td()}>{row.result || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {error ? <p style={{ margin: '12px 16px', fontSize: 12, color: ui?.danger || '#dc2626' }}>{error}</p> : null}
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default AccountingDocumentSaleReconcileModal;
